import { useState, useCallback } from 'react';
import { queryService } from '../../services/queryService';

/**
 * Hook to manage query timeline state and fetching
 * @param {string} queryId - Query ID
 * @returns {Object} Timeline state and handlers
 */
export const useQueryTimeline = (queryId) => {
  const [timelineEvents, setTimelineEvents] = useState([]);
  const [timelineCounts, setTimelineCounts] = useState({
    total: 0,
    auditEvents: 0,
    threadEvents: 0,
  });
  const [timelineLoading, setTimelineLoading] = useState(false);
  const [timelineError, setTimelineError] = useState('');

  const fetchTimeline = useCallback(async ({ silent = false } = {}) => {
    if (!queryId) return;

    setTimelineLoading(true);

    try {
      const { timeline: timelineData = [], counts = {} } = await queryService.getQueryTimeline(queryId);

      // Backend now returns unified and sorted timeline. 
      // We perform a minimal safety sort here but trust the backend's filtering.
      const sortedTimelineData = timelineData.sort((a = {}, b = {}) => {
        const aTime = a.performedAt ? new Date(a.performedAt).getTime() : 0;
        const bTime = b.performedAt ? new Date(b.performedAt).getTime() : 0;
        return bTime - aTime;
      });

      const filteredTimelineData = sortedTimelineData.filter((event = {}) => {
        const isEscalationStatusChanged =
          event.type === 'audit' &&
          event.action === 'status_changed' &&
          (
            event.newValue === 'escalated' ||
            event.meta?.queryStatus === 'escalated'
          );

        return !isEscalationStatusChanged;
      });

      setTimelineEvents(filteredTimelineData);
      setTimelineCounts({
        total: filteredTimelineData.length,
        auditEvents: filteredTimelineData.filter(e => e.type === 'audit').length,
        threadEvents: filteredTimelineData.filter(e => e.type === 'thread').length,
      });
      setTimelineError('');
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to load timeline events';
      setTimelineEvents([]);
      setTimelineCounts({
        total: 0,
        auditEvents: 0,
        threadEvents: 0,
      });

      if (!silent) {
        setTimelineError(message);
      } else {
        // console.error('Timeline fetch error:', error);
      }
    } finally {
      setTimelineLoading(false);
    }
  }, [queryId]);

  return {
    timelineEvents,
    timelineCounts,
    timelineLoading,
    timelineError,
    fetchTimeline,
  };
};
